/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {forwardRef, useContext} from 'react'
import {defineMessage, IntlContext} from 'react-intl'
import PropTypes from 'prop-types'
import {Icon, useTheme} from '@salesforce/retail-react-app/app/components/shared/ui'

// Our own SVG imports. These will be extracted to a single sprite sheet by the
// svg-sprite-loader webpack plugin at build time and injected in the <body> tag
// during SSR.
// NOTE: Another solution would be to use `require-context.macro` package to accomplish
// importing icon svg's.
import '@salesforce/retail-react-app/app/assets/svg/alert.svg'
import '@salesforce/retail-react-app/app/assets/svg/account.svg'
import '@salesforce/retail-react-app/app/assets/svg/apple.svg'
import '@salesforce/retail-react-app/app/assets/svg/basket.svg'
import '@salesforce/retail-react-app/app/assets/svg/check.svg'
import '@salesforce/retail-react-app/app/assets/svg/check-circle.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-up.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-down.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-right.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-left.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-right.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-up.svg'
import '@salesforce/retail-react-app/app/assets/svg/dashboard.svg'
import '@salesforce/retail-react-app/app/assets/svg/figma-logo.svg'
import '@salesforce/retail-react-app/app/assets/svg/filter.svg'
import '@salesforce/retail-react-app/app/assets/svg/file.svg'
import '@salesforce/retail-react-app/app/assets/svg/github-logo.svg'
import '@salesforce/retail-react-app/app/assets/svg/google.svg'
import '@salesforce/retail-react-app/app/assets/svg/hamburger.svg'
import '@salesforce/retail-react-app/app/assets/svg/info.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-facebook.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-instagram.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-twitter.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-youtube.svg'
import '@salesforce/retail-react-app/app/assets/svg/like.svg'
import '@salesforce/retail-react-app/app/assets/svg/lock.svg'
import '@salesforce/retail-react-app/app/assets/svg/plug.svg'
import '@salesforce/retail-react-app/app/assets/svg/receipt.svg'
import '@salesforce/retail-react-app/app/assets/svg/search.svg'
import '@salesforce/retail-react-app/app/assets/svg/signout.svg'
import '@salesforce/retail-react-app/app/assets/svg/user.svg'
import '@salesforce/retail-react-app/app/assets/svg/visibility.svg'
import '@salesforce/retail-react-app/app/assets/svg/visibility-off.svg'
import '@salesforce/retail-react-app/app/assets/svg/heart.svg'
import '@salesforce/retail-react-app/app/assets/svg/heart-solid.svg'
import '@salesforce/retail-react-app/app/assets/svg/close.svg'

// Custom Icons
import PaypalSymbol from '../../assets/svg/paypal-icon.svg'
import BrandLogoSymbol from '../../assets/svg/crunchyroll-Store-Logo.svg'
import WhiteArrowIcon from '../../assets/svg/arrow-white.svg'
import BlackArrowIcon from '../../assets/svg/arrow-black.svg'
import OrangeArrowIcon from '../../assets/svg/arrow-orange.svg'
import '../../assets/svg/Sort.svg'
import '../../assets/svg/Filter.svg'
import '../../assets/svg/FilterOrange.svg'
import '../../assets/svg/crown.svg'
import '../../assets/svg/shipping-icon.svg'
import '../../assets/svg/crunchy-store-icon.svg'
import '../../assets/svg/collapsed.svg'
import '../../assets/svg/expanded.svg'
import '../../assets/svg/radio-checked.svg'
import '../../assets/svg/store-icon.svg'
import '../../assets/svg/select-dropdown.svg'
import '../../assets/svg/add-to-wishlist.svg'
import '../../assets/svg/remove-from-wishlist.svg'
import '../../assets/svg/heart-icon-orange.svg'
import '../../assets/svg/enlarge-icon.svg'
import AUIcon from '../../assets/svg/countryFlags/flag-australia.svg' // AU - Australia
import USIcon from '../../assets/svg/countryFlags/flag-united-states.svg' // US - United States
import '../../assets/svg/bell-icon.svg'
import ShopingBagIcon from '../../assets/svg/shoping-bag-icon.svg'
import ArrowIconRight from '../../assets/svg/arrow-icon-right.svg'
import VideoControlButton from '../../assets/svg/video-control-button.svg'
import SearchbarIcon from '../../assets/svg/search-icon.svg'
import PremiumcrownIcon from '../../assets/svg/premium-crown-icon.svg'
import SearchArrowIcon from '../../assets/svg/search-arrow-icon.svg'
import DeleteBin from '../../assets/svg/trashBin-icon.svg'
import WhitePlusIcon from '../../assets/svg/white-plus-icon.svg'
import LeadingIcon from '../../assets/svg/leading-icon.svg'

// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
import AmexSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-amex.svg'
import CVVSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-cvv.svg'
import DiscoverSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-discover.svg'
import LocationSymbol from '@salesforce/retail-react-app/app/assets/svg/location.svg'
import MastercardSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-mastercard.svg'
import SocialPinterestSymbol from '@salesforce/retail-react-app/app/assets/svg/social-pinterest.svg'
import VisaSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-visa.svg'

// TODO: We're hardcoding the `viewBox` for these imported SVGs temporarily as the
// SVG loader plugin is not properly providing us the symbol data on the client side.
AmexSymbol.viewBox = AmexSymbol.viewBox || '0 0 38 22'
CVVSymbol.viewBox = CVVSymbol.viewBox || '0 0 41 24'
DiscoverSymbol.viewBox = DiscoverSymbol.viewBox || '0 0 38 22'
LocationSymbol.viewBox = LocationSymbol.viewBox || '0 0 16 21'
MastercardSymbol.viewBox = MastercardSymbol.viewBox || '0 0 38 22'
PaypalSymbol.viewBox = PaypalSymbol.viewBox || '0 0 80 20'
SocialPinterestSymbol.viewBox = SocialPinterestSymbol.viewBox || '0 0 21 20'
VisaSymbol.viewBox = VisaSymbol.viewBox || '0 0 38 22'
BrandLogoSymbol.viewBox = BrandLogoSymbol?.viewBox || '0 0 255 32'
WhiteArrowIcon.viewBox = WhiteArrowIcon?.viewBox || '0 0 40 40'
BlackArrowIcon.viewBox = BlackArrowIcon?.viewBox || '0 0 48 48'
OrangeArrowIcon.viewBox = OrangeArrowIcon?.viewBox || '0 0 40 40'
USIcon.viewBox = USIcon?.viewBox || '0 0 28 20'
AUIcon.viewBox = AUIcon?.viewBox || '0 0 28 20'
ShopingBagIcon.viewBox = ShopingBagIcon?.viewBox || '0 0 40 40'
ArrowIconRight.viewBox = ArrowIconRight?.viewBox || '0 0 48 48'
VideoControlButton.viewBox = VideoControlButton?.viewBox || '0 0 44 44'
SearchbarIcon.viewBox = SearchbarIcon?.viewBox || '0 0 52 48'
SearchArrowIcon.viewBox = SearchArrowIcon?.viewBox || '0 0 6 16'
DeleteBin.viewBox = DeleteBin?.viewBox || '0 0 20 20'
WhitePlusIcon.viewBox = WhitePlusIcon?.viewBox || '0 0 28 28'
LeadingIcon.viewBox = LeadingIcon?.viewBox || '0 0 32 32'

/**
 * A helper for creating a Chakra-wrapped icon from our own SVG imports via sprite sheet.
 * @param {string} name - the filename of the imported svg (does not include extension)
 * @param {Object} passProps - props that will be passed onto the underlying Icon component
 * @param {Object} localizationAttributes - attributes with localized values that will be passed
 *      onto the underlying Icon component, use `defineMessage` to create localized string.
 *      Additionally, if the icon is rendered outside the provider tree, you'll also need to
 *      pass an intl object from react-intl as a prop to translate the messages.
 */
/* istanbul ignore next */
export const icon = (name, passProps, localizationAttributes) => {
    const displayName = name
        .toLowerCase()
        .replace(/(?:^|[\s-/])\w/g, (match) => match.toUpperCase())
        .replace(/-/g, '')
    const component = forwardRef((props, ref) => {
        const theme = useTheme()
        // NOTE: We want to avoid `useIntl` here because that throws when <IntlProvider> is not in
        // the component ancestry, but we only enforce `intl` if we have `localizationAttributes`.
        let intl = useContext(IntlContext)
        if (localizationAttributes) {
            if (props?.intl) {
                const {intl: intlProp, ...otherProps} = props
                // Allow `props.intl` to take precedence over the intl we found
                intl = intlProp
                props = otherProps
            }
            if (!intl) {
                throw new Error(
                    'To localize messages, you must either have <IntlProvider> in the component ancestry or provide `intl` as a prop'
                )
            }
            Object.keys(localizationAttributes).forEach((key) => {
                passProps[key] = intl.formatMessage(localizationAttributes[key])
            })
        }
        const baseStyle = theme?.components?.Icon?.baseStyle
        return (
            <Icon ref={ref} role="img" aria-label={name} {...baseStyle} {...props} {...passProps}>
                <use role="presentation" xlinkHref={`#${name}`} />
            </Icon>
        )
    })

    component.propTypes = {
        intl: PropTypes.object
    }

    component.displayName = `${displayName}Icon`
    return component
}

// Export Chakra icon components that use our SVG sprite symbol internally
// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
export const AccountIcon = icon('account')
export const AlertIcon = icon('alert')
export const AmexIcon = icon('cc-amex', {viewBox: AmexSymbol.viewBox})
export const AppleIcon = icon('apple')
export const BasketIcon = icon('basket')
export const CheckIcon = icon('check')
export const CheckCircleIcon = icon('check-circle')
export const ChevronDownIcon = icon('chevron-down')
export const ChevronLeftIcon = icon('chevron-left')
export const ChevronRightIcon = icon('chevron-right')
export const ChevronUpIcon = icon('chevron-up')
export const CVVIcon = icon('cc-cvv', {viewBox: CVVSymbol.viewBox})
export const CloseIcon = icon('close')
export const DashboardIcon = icon('dashboard')
export const DiscoverIcon = icon('cc-discover', {viewBox: DiscoverSymbol.viewBox})
export const FigmaLogo = icon('figma-logo')
export const FilterIcon = icon('filter')
export const FileIcon = icon('file')
export const GithubLogo = icon('github-logo')
export const GoogleIcon = icon('google')
export const HamburgerIcon = icon('hamburger')
export const HeartIcon = icon('heart')
export const HeartIconBlack = icon('add-to-wishlist')
export const HeartIconRed = icon('remove-from-wishlist')
export const HeartIconOrange = icon('heart-icon-orange')
export const EnlargeIcon = icon('enlarge-icon')
export const HeartSolidIcon = icon('heart-solid')
export const InfoIcon = icon('info')
export const LikeIcon = icon('like')
export const LockIcon = icon(
    'lock',
    {
        'aria-hidden': false,
        focusable: true
    },
    {
        'aria-label': defineMessage({
            id: 'icons.assistive_msg.lock',
            defaultMessage: 'Secure'
        })
    }
)
export const LocationIcon = icon('location')
export const PlugIcon = icon('plug')
export const MastercardIcon = icon('cc-mastercard', {viewBox: MastercardSymbol.viewBox})
export const RadioCheckedIcon = icon('radio-checked')
export const ReceiptIcon = icon('receipt')
export const SearchIcon = icon('search', {'aria-hidden': true})
export const SocialFacebookIcon = icon('social-facebook')
export const SocialInstagramIcon = icon('social-instagram')
export const SocialPinterestIcon = icon('social-pinterest', {
    viewBox: SocialPinterestSymbol.viewBox
})
export const SocialTwitterIcon = icon('social-twitter')
export const SocialYoutubeIcon = icon('social-youtube')
export const SignoutIcon = icon('signout')
export const UserIcon = icon('user')
export const VisaIcon = icon('cc-visa', {viewBox: VisaSymbol.viewBox})
export const VisibilityIcon = icon('visibility')
export const VisibilityOffIcon = icon('visibility-off')
export const CrownIcon = icon('crown')
export const ShippingIcon = icon('shipping-icon')
export const CrunchyStoreIcon = icon('crunchy-store-icon')
export const BellIcon = icon('bell-icon')

// Custom Icons calling
export const BrandLogo = icon('crunchyroll-Store-Logo', {
    viewBox: BrandLogoSymbol.viewBox,
    width: '255px',
    height: '32px',
    alt: 'Logo of CrunchyRoll Store'
})

export const WhiteArrow = icon('arrow-white', {
    viewBox: WhiteArrowIcon.viewBox,
    width: '40px',
    height: '40px'
})
export const BlackArrow = icon('arrow-black', {
    viewBox: BlackArrowIcon.viewBox,
    width: '40px',
    height: '40px'
})
export const OrangeArrow = icon('arrow-orange', {
    viewBox: '0 0 24 24'
})
export const ShopingBag = icon('shoping-bag-icon', {
    viewBox: ShopingBagIcon.viewBox,
    width: '40px',
    height: '40px'
})
export const ArrowIcon = icon('arrow-icon-right', {
    viewBox: ArrowIconRight.viewBox,
    width: '48px',
    height: '48px'
})
export const VideoControlIcon = icon('video-control-button', {
    viewBox: VideoControlButton.viewBox,
    width: '44px',
    height: '44px'
})
export const SearchBoxIcon = icon('search-icon', {
    viewBox: SearchbarIcon.viewBox,
    width: '52px',
    height: '48px'
})
export const PremiumCrownIcon = icon('premium-crown-icon', {
    viewBox: PremiumcrownIcon.viewBox,
    width: '20px',
    height: '20px'
})

export const SortIcon = icon('Sort')
export const FilterIconCR = icon('Filter')
export const FilterOrange = icon('FilterOrange')
export const PaypalIcon = icon('paypal', {viewBox: PaypalSymbol.viewBox})
export const PlusIcon = icon('collapsed')
export const MinusIcon = icon('expanded')
export const StoreIcon = icon('store-icon')
export const SelectDropdown = icon('select-dropdown')
export const FlagAUIcon = icon('flag-australia', {
    viewBox: AUIcon.viewBox,
    width: '28px',
    height: '20px'
})
export const FlagUSIcon = icon('flag-united-states', {
    viewBox: USIcon.viewBox,
    width: '28px',
    height: '20px'
})
export const SearchArrowWhiteIcon = icon('search-arrow-icon', {
    viewBox: SearchArrowIcon.viewBox,
    width: '6px',
    height: '16px'
})
export const DeleteBinIcon = icon('trashBin-icon', {
    viewBox: DeleteBin.viewBox,
    width: '20px',
    height: '20px'
})
export const WhitePlusBtnIcon = icon('white-plus-icon', {
    viewBox: WhitePlusIcon.viewBox,
    width: '28px',
    height: '28px'
})
export const LeadingIconSvg = icon('leading-icon', {
    viewBox: LeadingIcon.viewBox,
    width: '32px',
    height: '32px'
})
